import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { Loader2, ShieldOff, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import AdminCharts from '@/components/admin/AdminCharts';
import ExportExcelButton from '@/components/admin/ExportExcelButton';
import ConsumerFunnelOverview from '@/components/admin/ConsumerFunnelOverview';
import DashboardLiveOverview from '@/components/admin/DashboardLiveOverview';
import QuickAccessGrid from '@/components/admin/QuickAccessGrid';
import QuickAccessCustomizer from '@/components/admin/QuickAccessCustomizer';
import { useToast } from '@/components/ui/use-toast';
import { ADMIN_NAV } from '@/components/admin/adminNav';

// Every admin area is a selectable Quick Access tile, keyed by its route.
// The grid holds a maximum of 16 tiles (4 rows of 4).
const QUICK_ACCESS_MAX = 16;
const QUICK_ACCESS_FIELD = 'dashboard_quick_access';
const QUICK_ACCESS_TILES = ADMIN_NAV.flatMap((section) =>
  section.items.map((item) => ({ ...item, group: section.group }))
);
const TILE_BY_KEY = new Map(QUICK_ACCESS_TILES.map((tile) => [tile.to, tile]));
// Default layout keeps the dashboard's original tiles — the first two areas of
// each nav group.
const DEFAULT_QUICK_ACCESS = ADMIN_NAV.flatMap((s) => s.items.slice(0, 2)).map((i) => i.to);

export default function AdminDashboard() {
  const { user: currentUser, isLoadingAuth, refreshUser } = useAuth();
  const { toast } = useToast();
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);
  const [quickAccess, setQuickAccess] = useState(null);
  const [editingQuickAccess, setEditingQuickAccess] = useState(false);
  const [customizerOpen, setCustomizerOpen] = useState(false);
  const [savingQuickAccess, setSavingQuickAccess] = useState(false);

  // Load this admin's saved tile layout from their own user record, falling
  // back to the default layout when nothing has been saved yet.
  useEffect(() => {
    if (!currentUser) return;
    const saved = Array.isArray(currentUser[QUICK_ACCESS_FIELD]) ? currentUser[QUICK_ACCESS_FIELD] : null;
    const list = (saved || DEFAULT_QUICK_ACCESS).filter((key) => TILE_BY_KEY.has(key));
    setQuickAccess(list.slice(0, QUICK_ACCESS_MAX));
  }, [currentUser?.id]);

  // Save the tile list (order + visibility) on the user record so the layout is
  // preserved per admin, on every login and device.
  const persistQuickAccess = async (next) => {
    setQuickAccess(next);
    setSavingQuickAccess(true);
    try {
      await base44.auth.updateMe({ [QUICK_ACCESS_FIELD]: next });
      await refreshUser();
    } catch (e) {
      toast({ title: 'Could not save your dashboard layout', description: e.message, variant: 'destructive' });
    }
    setSavingQuickAccess(false);
  };

  const toggleQuickTile = (key) => {
    if (!quickAccess) return;
    if (quickAccess.includes(key)) persistQuickAccess(quickAccess.filter((k) => k !== key));
    else if (quickAccess.length < QUICK_ACCESS_MAX) persistQuickAccess([...quickAccess, key]);
  };

  const reorderQuickTiles = (from, to) => {
    const next = [...quickAccess];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    persistQuickAccess(next);
  };

  const handleReconcile = async () => {
    setReconciling(true);
    setReconcileResult(null);
    try {
      const res = await base44.functions.invoke('adminReconcileOrders', {});
      setReconcileResult(res.data);
    } catch (err) {
      setReconcileResult({ error: 'Reconciliation failed.' });
    }
    setReconciling(false);
  };

  if (isLoadingAuth) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
          <p className="text-white/20 text-xs mt-2">You must be an admin to view this page.</p>
          {!currentUser && (
            <Link to="/admin" className="inline-block mt-5 text-primary text-xs font-bold tracking-widest hover:underline">SIGN IN →</Link>
          )}
        </div>
      </div>
    );
  }

  const quickTiles = (quickAccess || []).map((key) => TILE_BY_KEY.get(key)).filter(Boolean);

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Header */}
      <section className="py-10 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <p className="text-primary text-xs font-bold tracking-widest mb-2">ADMIN AREA</p>
          <h1 className="text-white font-black text-3xl md:text-4xl uppercase">ADMIN DASHBOARD</h1>
          <p className="text-white/30 text-xs mt-2">
            Logged in as: <span className="text-white font-medium">{currentUser.first_name || currentUser.full_name?.split(' ')[0]} ({currentUser.email})</span>
          </p>

          {/* Quick actions */}
          <div className="flex flex-wrap items-center gap-2 mt-5">
            <button
              onClick={handleReconcile}
              disabled={reconciling}
              className="flex items-center gap-2 border border-white/10 hover:border-green-500/50 text-white/50 hover:text-green-400 text-xs font-bold tracking-wider px-4 py-2.5 transition-all disabled:opacity-40"
            >
              {reconciling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              RECONCILE PAYMENTS
            </button>
            <ExportExcelButton />
          </div>

          {reconcileResult && (
            <div
              className={`mt-4 px-4 py-2.5 text-xs font-medium border ${
                reconcileResult.error
                  ? 'border-red-500/20 bg-red-500/5 text-red-400'
                  : 'border-green-500/20 bg-green-500/5 text-green-400'
              }`}
            >
              {reconcileResult.error
                ? reconcileResult.error
                : `✓ Checked ${reconcileResult.checked} payments · ${reconcileResult.reconciled} new order(s) recovered · ${reconcileResult.already_exists} already matched`}
            </div>
          )}
        </div>
      </section>

      {/* Quick access — per-admin, customisable tile layout */}
      {quickAccess && (
        <QuickAccessGrid
          items={quickTiles}
          editing={editingQuickAccess}
          onEditingChange={setEditingQuickAccess}
          onReorder={reorderQuickTiles}
          onRemove={toggleQuickTile}
          onOpenCustomizer={() => setCustomizerOpen(true)}
          saving={savingQuickAccess}
          max={QUICK_ACCESS_MAX}
        />
      )}

      <QuickAccessCustomizer
        open={customizerOpen}
        onClose={() => setCustomizerOpen(false)}
        selected={quickAccess || []}
        onToggle={toggleQuickTile}
        max={QUICK_ACCESS_MAX}
      />

      {/* Live overview */}
      <section className="px-6 lg:px-10 pb-4 max-w-6xl mx-auto">
        <p className="text-white/30 text-[10px] font-bold tracking-widest mb-4">LIVE OVERVIEW</p>
        <DashboardLiveOverview />
      </section>

      {/* Consumer funnel & loyalty */}
      <section className="px-6 lg:px-10 py-4 max-w-6xl mx-auto">
        <p className="text-white/30 text-[10px] font-bold tracking-widest mb-4">CONSUMER FUNNEL & LOYALTY</p>
        <ConsumerFunnelOverview />
      </section>

      {/* Financial & booking summary */}
      <section className="px-6 lg:px-10 py-4 max-w-6xl mx-auto">
        <p className="text-white/30 text-[10px] font-bold tracking-widest mb-4">FINANCIAL & BOOKING SUMMARY</p>
        <AdminCharts />
      </section>
    </div>
  );
}